import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

const THIRD_PARTY_CART_ADD_SOURCE = 'third-party-cart-add-bridge';
const THIRD_PARTY_CART_ADD_SYNC_DELAY = 2500;
const THIRD_PARTY_CART_ADD_POLL_INTERVAL = 500;
const THIRD_PARTY_CART_ADD_POLL_TIMEOUT = 10000;

/**
 * Checks whether a request URL points at Shopify's AJAX cart add endpoint.
 * @param {RequestInfo | URL | string | undefined} input
 * @returns {boolean}
 */
function isCartAddRequest(input) {
  if (!input) return false;

  const requestUrl = input instanceof Request ? input.url : input.toString();
  const url = new URL(requestUrl, window.location.origin);
  const cartAddPath = Theme.routes.cart_add_url;

  return (
    url.origin === window.location.origin &&
    (url.pathname === cartAddPath || url.pathname === `${cartAddPath}.js`)
  );
}

/**
 * Horizon's native add-to-cart request already includes section rendering and dispatches its own cart event.
 * @param {BodyInit | null | undefined} body
 * @returns {boolean}
 */
function hasSectionRenderingPayload(body) {
  if (!body) return false;

  if (body instanceof FormData || body instanceof URLSearchParams) {
    return body.has('sections');
  }

  if (typeof body !== 'string') return false;

  if (body.includes('sections=')) return true;

  try {
    const parsedBody = JSON.parse(body);
    return Boolean(parsedBody?.sections);
  } catch (_) {
    return false;
  }
}

const THEME_FALLBACK_ENGRAVING_SOURCE = 'theme-fallback';
const THEME_FALLBACK_CHARGE_SYNC_SUPPRESS_MS = 5000;

/**
 * Detects theme-initiated fallback engraving charge adds so they do not re-trigger cart sync.
 * @param {BodyInit | null | undefined} body
 * @returns {boolean}
 */
function isThemeFallbackEngravingChargePayload(body) {
  if (typeof body !== 'string') return false;

  try {
    const parsedBody = JSON.parse(body);
    const items = parsedBody?.items;

    if (!Array.isArray(items) || items.length === 0) return false;

    return items.every((item) => item?.properties?._engraving_source === THEME_FALLBACK_ENGRAVING_SOURCE);
  } catch (_) {
    return false;
  }
}

/**
 * Lets third-party personalizer apps that add to cart outside Horizon's product form trigger the drawer flow.
 */
function installThirdPartyCartAddBridge() {
  if (window.themeThirdPartyCartAddBridgeInstalled) return;
  window.themeThirdPartyCartAddBridgeInstalled = true;

  let syncTimeout;
  let cartWatchTimeout;
  let cartWatchStartedAt = 0;
  let cartWatchInitialCount;
  let isSyncingThirdPartyCart = false;
  let nativeCartAddObservedAt = 0;
  let themeFallbackChargeAddAt = 0;

  const shouldSuppressCartSync = () =>
    Date.now() - themeFallbackChargeAddAt < THEME_FALLBACK_CHARGE_SYNC_SUPPRESS_MS;

  const closePersonalizerModal = () => {
    const closeButton = document.querySelector('.pplr_close, .js-modal-close, [data-pplr-close]');
    if (closeButton instanceof HTMLElement) {
      closeButton.click();
    }
  };

  const scheduleCartSync = () => {
    clearTimeout(syncTimeout);

    syncTimeout = setTimeout(async () => {
      try {
        isSyncingThirdPartyCart = true;
        const cart = await ensureMissingEngravingCharges(await fetchCart());

        document.dispatchEvent(
          new CartAddEvent(cart, THIRD_PARTY_CART_ADD_SOURCE, {
            source: THIRD_PARTY_CART_ADD_SOURCE,
            itemCount: cart.item_count,
          })
        );
        closePersonalizerModal();
      } catch (error) {
        console.error('Failed to sync third-party cart add:', error);
      } finally {
        isSyncingThirdPartyCart = false;
      }
    }, THIRD_PARTY_CART_ADD_SYNC_DELAY);
  };

  const fetchCart = async () => {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    return response.json();
  };

  const getCartAddJsonUrl = () =>
    Theme.routes.cart_add_url.endsWith('.js') ? Theme.routes.cart_add_url : `${Theme.routes.cart_add_url}.js`;

  const getVisibleCartCount = () => {
    const cartBubble = document.querySelector('[data-testid="cart-bubble"]');
    return Number(cartBubble?.textContent?.trim()) || 0;
  };

  const isEngravingChargeLine = (item) =>
    item?.handle === 'engraving-charge' || item?.product_title?.toLowerCase() === 'engraving charge';

  const hasEngravingProperties = (item) => {
    const properties = item?.properties || {};

    return Boolean(
      properties._pplr_addprice ||
        properties['Engraving Type'] ||
        properties.Standard ||
        properties.Extended ||
        properties['Mixed Text'] ||
        properties.Initials ||
        properties.Monogram ||
        properties.Font
    );
  };

  const getEngravingParentKey = (chargeItem) => {
    const parentKey = chargeItem?.properties?._engraving_parent_key;
    return typeof parentKey === 'string' && parentKey.length > 0 ? parentKey : null;
  };

  const findKeyLinkedChargeForProduct = (cartItems, productItem) =>
    cartItems.find(
      (cartItem) => isEngravingChargeLine(cartItem) && getEngravingParentKey(cartItem) === productItem.key
    );

  const getEngravedProducts = (cartItems) =>
    cartItems.filter((item) => !isEngravingChargeLine(item) && hasEngravingProperties(item));

  const getEngravingChargeLines = (cartItems) => cartItems.filter(isEngravingChargeLine);

  /**
   * Products that still need a fallback charge: fewer charge lines exist than engraved
   * products, after key-linked and legacy adjacent unlinked charges are accounted for.
   * @param {Array<object>} cartItems
   * @returns {Array<object>}
   */
  const getProductsNeedingFallbackCharge = (cartItems) => {
    const engravedProducts = getEngravedProducts(cartItems);
    const chargeLines = getEngravingChargeLines(cartItems);

    if (engravedProducts.length === 0 || chargeLines.length >= engravedProducts.length) return [];

    const unclaimedChargeKeys = new Set(chargeLines.map((charge) => charge.key));
    const productsStillNeedingCharge = [];

    for (let index = 0; index < cartItems.length; index++) {
      const item = cartItems[index];

      if (isEngravingChargeLine(item) || !hasEngravingProperties(item)) continue;

      const keyLinkedCharge = findKeyLinkedChargeForProduct(cartItems, item);
      if (keyLinkedCharge) {
        unclaimedChargeKeys.delete(keyLinkedCharge.key);
        continue;
      }

      const prev = cartItems[index - 1];
      const next = cartItems[index + 1];
      let matchedCharge = false;

      for (const neighbor of [prev, next]) {
        if (!neighbor || !isEngravingChargeLine(neighbor) || !unclaimedChargeKeys.has(neighbor.key)) continue;

        const parentKey = getEngravingParentKey(neighbor);
        if (parentKey && parentKey !== item.key) continue;

        unclaimedChargeKeys.delete(neighbor.key);
        matchedCharge = true;
        break;
      }

      if (!matchedCharge) productsStillNeedingCharge.push(item);
    }

    const chargeDeficit = engravedProducts.length - chargeLines.length;
    return productsStillNeedingCharge.slice(0, chargeDeficit);
  };

  const getEngravingChargeAmount = (item) => {
    const addPrice = Number(item.properties?._pplr_addprice);
    if (Number.isFinite(addPrice) && addPrice > 0) return addPrice;

    const engravingType = item.properties?.['Engraving Type'];
    const priceMatch = typeof engravingType === 'string' ? engravingType.match(/\+?\$(\d+)/) : null;
    return priceMatch?.[1] ? Number(priceMatch[1]) * 100 : 0;
  };

  const getEngravingType = (item) => {
    const engravingType = item.properties?.['Engraving Type'];
    if (typeof engravingType !== 'string') return '';

    return engravingType.replace(/\s*\+\$\d+.*/, '').trim().toLowerCase();
  };

  const fetchEngravingChargeProduct = async () => {
    const response = await fetch('/products/engraving-charge.js');
    return response.ok ? response.json() : null;
  };

  const findEngravingChargeVariant = (chargeProduct, cartItem) => {
    const engravingChargeAmount = getEngravingChargeAmount(cartItem);
    const engravingType = getEngravingType(cartItem);
    if (!engravingChargeAmount || !engravingType || !chargeProduct?.variants) return null;

    return chargeProduct.variants.find((variant) => {
      const variantTitle = variant.title.toLowerCase();
      return variant.price === engravingChargeAmount && variantTitle.includes(engravingType);
    });
  };

  const ensureMissingEngravingCharges = async (cart) => {
    const cartItems = cart?.items || [];
    const customizedItemsMissingCharge = getProductsNeedingFallbackCharge(cartItems);

    if (!customizedItemsMissingCharge.length) return cart;

    const chargeProduct = await fetchEngravingChargeProduct();
    const chargeItems = customizedItemsMissingCharge
      .map((item) => {
        const chargeVariant = findEngravingChargeVariant(chargeProduct, item);
        if (!chargeVariant) return null;

        return {
          id: chargeVariant.id,
          quantity: item.quantity,
          properties: {
            _engraving_parent_key: item.key,
            _engraving_parent_variant_id: item.variant_id,
            _engraving_source: THEME_FALLBACK_ENGRAVING_SOURCE,
          },
        };
      })
      .filter(Boolean);

    if (!chargeItems.length) return cart;

    themeFallbackChargeAddAt = Date.now();

    const addChargeResponse = await fetch(getCartAddJsonUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items: chargeItems }),
    });

    if (!addChargeResponse.ok) return cart;

    return fetchCart();
  };

  const pollForCartAdd = async () => {
    try {
      const cart = await fetchCart();
      if (cart.item_count !== cartWatchInitialCount) {
        scheduleCartSync();
        return;
      }
    } catch (_) {
      // Keep polling briefly; third-party cart requests can still be in flight.
    }

    if (Date.now() - cartWatchStartedAt >= THIRD_PARTY_CART_ADD_POLL_TIMEOUT) return;

    cartWatchTimeout = setTimeout(pollForCartAdd, THIRD_PARTY_CART_ADD_POLL_INTERVAL);
  };

  const startCartAddWatch = () => {
    clearTimeout(cartWatchTimeout);
    cartWatchInitialCount = getVisibleCartCount();
    cartWatchStartedAt = Date.now();
    cartWatchTimeout = setTimeout(pollForCartAdd, THIRD_PARTY_CART_ADD_POLL_INTERVAL);
  };

  if ('PerformanceObserver' in window) {
    const cartAddObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (Date.now() - nativeCartAddObservedAt < 3000) continue;
        if (shouldSuppressCartSync()) continue;
        if (isCartAddRequest(entry.name)) {
          scheduleCartSync();
        }
      }
    });

    cartAddObserver.observe({ type: 'resource', buffered: true });
  }

  document.addEventListener(
    'click',
    (event) => {
      const target =
        event.target instanceof Element
          ? event.target
          : event.target instanceof Node
            ? event.target.parentElement
            : null;
      const personalizerAddButton = target?.closest('button.pplr-btn[name="add"], button[name="add"].pplr-c-button');

      if (personalizerAddButton) {
        startCartAddWatch();
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const target = event.target instanceof HTMLFormElement ? event.target : null;
      if (target?.querySelector('button.pplr-btn[name="add"], button[name="add"].pplr-c-button')) {
        startCartAddWatch();
      }
    },
    true
  );

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const shouldSyncCart =
      !isSyncingThirdPartyCart &&
      !shouldSuppressCartSync() &&
      isCartAddRequest(input) &&
      !hasSectionRenderingPayload(init.body) &&
      !isThemeFallbackEngravingChargePayload(init.body);
    if (isCartAddRequest(input) && hasSectionRenderingPayload(init.body)) {
      nativeCartAddObservedAt = Date.now();
    }
    const response = await originalFetch(input, init);

    if (shouldSyncCart && response.ok) {
      scheduleCartSync();
    }

    return response;
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...args) {
    this.themeShouldSyncCartAdd = method.toUpperCase() === 'POST' && isCartAddRequest(url);
    return originalOpen.call(this, method, url, ...args);
  };

  XMLHttpRequest.prototype.send = function (body) {
    const shouldSyncCart =
      !isSyncingThirdPartyCart &&
      !shouldSuppressCartSync() &&
      this.themeShouldSyncCartAdd &&
      !hasSectionRenderingPayload(body) &&
      !isThemeFallbackEngravingChargePayload(body);

    if (shouldSyncCart) {
      this.addEventListener(
        'loadend',
        () => {
          if (this.status >= 200 && this.status < 300) {
            scheduleCartSync();
          }
        },
        { once: true }
      );
    }

    return originalSend.call(this, body);
  };
}

installThirdPartyCartAddBridge();

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    const url = new URL(window.location.href);
    if (url.searchParams.get('cart') === 'open') {
      url.searchParams.delete('cart');
      history.replaceState(null, '', url);
      this.open();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
